/* First-shift guided checklist for brand-new stores: Dashboard card + header progress pill. Steps auto-complete from real data. */
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, ArrowRight, X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { t } from "@/i18n";
import { useLegacyApi, errMsg } from "@/hooks/useLegacy";

export const useOnboarding = () => {
  const api = useLegacyApi();
  const [data, setData] = useState(null);
  const load = useCallback(() => { if (api.storeId) api.get("/onboarding").then(setData).catch(() => setData(null)); }, [api]);
  useEffect(load, [load]);
  const dismiss = async (undo = false) => {
    try { await api.post(`/onboarding/dismiss?undo=${undo}`); load(); toast.success(undo ? t("Setup guide is back on your Dashboard") : t("Setup guide hidden")); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return { data, dismiss, reload: load };
};

export const OnboardingPill = ({ data }) => {
  if (!data?.show) return null;
  return (
    <Link to="/dashboard" data-testid="onboarding-pill" className="hidden sm:inline-flex items-center gap-2 rounded-full bg-[#663399]/10 text-[#663399] pl-2 pr-3 py-1 text-xs font-bold hover:bg-[#663399]/15 transition-colors">
      <Sparkles className="w-3.5 h-3.5" />{t("Setup")} {data.completed}/{data.total}
      <span className="w-16 h-1.5 rounded-full bg-[#663399]/15 overflow-hidden"><span className="block h-full bg-[#663399] transition-all" style={{ width: `${(data.completed / data.total) * 100}%` }} /></span>
    </Link>
  );
};

const Step = ({ s, i, next }) => (
  <div data-testid={`onboarding-step-${s.key}`} className={`flex items-start gap-3 rounded-xl border p-3.5 transition-colors ${s.done ? "border-emerald-200 bg-emerald-50/60" : next ? "border-[#663399]/40 bg-[#663399]/[0.04] shadow-sm" : "border-gray-200 bg-white"}`}>
    {s.done ? <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0 mt-0.5" /> : <Circle className={`w-6 h-6 shrink-0 mt-0.5 ${next ? "text-[#663399]" : "text-gray-300"}`} />}
    <div className="min-w-0 flex-1">
      <div className={`text-sm font-bold ${s.done ? "text-emerald-800 line-through decoration-emerald-400" : "text-gray-800"}`}><span className="text-gray-400 font-semibold mr-1.5">{i + 1}.</span>{t(s.title)}</div>
      <div className="text-xs text-gray-500 mt-0.5">{t(s.text)}</div>
    </div>
    {!s.done && (
      <Link to={s.to} data-testid={`onboarding-go-${s.key}`} className={`shrink-0 inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-bold transition-transform hover:-translate-y-px ${next ? "bg-[#663399] text-white" : "border border-gray-200 text-gray-600 bg-white"}`}>{t(s.cta)}<ArrowRight className="w-3.5 h-3.5" /></Link>
    )}
  </div>
);

export const OnboardingCard = ({ data, onDismiss }) => {
  if (!data?.show) return null;
  const nextKey = data.steps.find((s) => !s.done)?.key;
  return (
    <div data-testid="onboarding-card" className="tp-fade mb-4 rounded-2xl border border-[#663399]/20 bg-white shadow-[0_12px_32px_-16px_rgba(102,51,153,0.35)] overflow-hidden">
      <div className="flex items-start gap-3 px-5 pt-5 pb-3">
        <span className="w-10 h-10 rounded-xl bg-[#663399] text-white grid place-items-center shrink-0"><Sparkles className="w-5 h-5" /></span>
        <div className="min-w-0 flex-1">
          <h2 className="text-base md:text-lg font-extrabold text-gray-900">{t("Welcome to TallyPage")}{data.store_name ? ` · ${data.store_name}` : ""}</h2>
          <p className="text-sm text-gray-500 mt-0.5">{data.mode === "money" ? t("A few quick steps to get your books running. Each one ticks off automatically as you go.") : t("Four quick steps to complete your first shift. Each one ticks off automatically as you go.")}</p>
        </div>
        <button data-testid="onboarding-skip" onClick={() => onDismiss(false)} className="shrink-0 inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold text-gray-500 hover:bg-gray-100 transition-colors"><X className="w-3.5 h-3.5" />{t("Skip tour")}</button>
      </div>
      <div className="px-5 pb-2 flex items-center gap-3">
        <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden"><div data-testid="onboarding-progress" className="h-full bg-[#663399] transition-all" style={{ width: `${(data.completed / data.total) * 100}%` }} /></div>
        <span className="text-xs font-bold text-[#663399] tabular-nums">{data.completed}/{data.total} {t("done")}</span>
      </div>
      <div className="px-5 pb-5 grid md:grid-cols-2 gap-3 tp-stagger">
        {data.steps.map((s, i) => <Step key={s.key} s={s} i={i} next={s.key === nextKey} />)}
      </div>
    </div>
  );
};
